import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
  RoleDto,
  RoleListSmallDto,
} from '../../../role-list/models/role-models';
import { CreateUserDto } from '../../models/user-models';
import { ModalService } from '../../../../services/modal.service';
import { Subscription } from 'rxjs';
import { fullNameValidator } from '../../../../validators/fullName.validator';
import { UserDataService } from '../../services/user-data.service';

export interface StatusList {
  name: string;
  state: boolean;
}

@Component({
  selector: 'app-add-user-modal',
  templateUrl: './add-user-modal.component.html',
  styleUrls: ['./add-user-modal.component.scss'],
})
export class AddUserModalComponent implements OnInit, OnDestroy {
  @Input() roleList: RoleListSmallDto[] = [];

  @Output() addedUser: EventEmitter<void> = new EventEmitter<void>();

  form!: FormGroup;
  twoFactor = false;
  isOpenStatusSelect = false;
  isOpenRoleSelect = false;

  selectedStatus: boolean | undefined;
  selectedRole: number[] = [];

  subs: Subscription[] = [];

  isSending = false;

  statusList: StatusList[] = [
    { name: 'ACTIVE', state: true },
    { name: 'BLOCKED', state: false },
  ];

  @HostListener('document:click', ['$event'])
  onClick(event: Event) {
    if (this.el.nativeElement.contains(event.target)) {
      this.isOpenStatusSelect = false;
      this.isOpenRoleSelect = false;
    }
  }

  constructor(
    private el: ElementRef,
    private translateService: TranslateService,
    public modalService: ModalService,
    private userDataService: UserDataService
  ) {}

  ngOnInit() {
    this.initForm();
    this.afterClosed();
  }

  ngOnDestroy() {
    this.subs.forEach((item) => item.unsubscribe());
  }

  afterClosed() {
    this.subs.push(
      this.modalService.isModalOpen$.subscribe({
        next: (res) => {
          if (!res) {
            this.twoFactor = false;
            this.form.reset();
          }
        },
      })
    );
  }

  initForm(): void {
    this.form = new FormGroup({
      fullNameFormControl: new FormControl('', [
        Validators.required,
        fullNameValidator(),
      ]),
      phoneFormControl: new FormControl('', [
        Validators.required,
        Validators.minLength(10),
      ]),
      emailFormControl: new FormControl('', [
        Validators.required,
        Validators.email,
      ]),
      statusFormControl: new FormControl('', [Validators.required]),
      roleFormControl: new FormControl('', [Validators.required]),
    });
  }

  get fullNameRequireError() {
    return this.form.get('fullNameFormControl')?.hasError('required');
  }

  get fullNameFormatError() {
    return this.form.get('fullNameFormControl')?.hasError('fullNameNotValid');
  }

  get phoneFormatError() {
    return this.form.get('phoneFormControl')?.hasError('minlength');
  }

  get emailFormatError() {
    return this.form.get('emailFormControl')?.hasError('required');
  }

  get emailRequireFormatError() {
    return this.form.get('emailFormControl')?.hasError('email');
  }

  get statusFormatError() {
    return this.form.get('statusFormControl')?.hasError('required');
  }

  get roleFormatError() {
    return this.form.get('roleFormControl')?.hasError('required');
  }

  openStatusSelect(event: MouseEvent) {
    event.stopPropagation();
    this.isOpenRoleSelect = false;
    this.isOpenStatusSelect = true;
  }

  openRoleSelect(event: MouseEvent) {
    event.stopPropagation();
    this.isOpenStatusSelect = false;
    this.isOpenRoleSelect = true;
  }

  setStatus(item: StatusList) {
    this.selectedStatus = item.state;
    this.translateService.get(`USER_LIST.MODAL.${item.name}`).subscribe({
      next: (res) => {
        this.form.patchValue({ statusFormControl: res });
      },
    });
  }

  setRole(role: RoleListSmallDto) {
    this.selectedRole = [];
    this.selectedRole.push(role.id);
    this.form.patchValue({ roleFormControl: role.name });
  }

  sendForm() {
    this.isSending = true;
    const userData: CreateUserDto = {
      full_name: this.form.get('fullNameFormControl')?.value,
      email: this.form.get('emailFormControl')?.value,
      phone_number: `+7${this.form.get('phoneFormControl')?.value}`,
      is_enabled: this.selectedStatus,
      two_factor_auth: this.twoFactor,
      role_id: this.selectedRole.join(),
    };
    this.userDataService.createUser(userData).subscribe({
      next: () => {
        this.modalService.closeModal();
        this.addedUser.emit();
        this.isSending = false;
      },
      error: () => {
        this.isSending = false;
      },
    });
  }
}
